import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowUpRight } from 'lucide-react';
import { FadeUp, TextReveal } from '@/components/site/Reveal';
import { fetchProjects, STATIC } from '@/lib/cms';
import Pagination from '@/components/site/Pagination';
import SEO from '@/components/site/SEO';

const PER_PAGE = 6;

export default function Portfolio() {
  const [items, setItems] = useState(STATIC.projects);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('All');
  const [page, setPage] = useState(1);

  useEffect(() => { fetchProjects().then(setItems); }, []);
  useEffect(() => { setPage(1); }, [q, cat]);

  const categories = useMemo(() => ['All', ...Array.from(new Set(items.map(i => i.category)))], [items]);
  const filtered = items.filter(i => (cat === 'All' || i.category === cat) && (q === '' || i.title.toLowerCase().includes(q.toLowerCase())));
  const paged = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  return (
    <>
      <SEO title="Work — Slinkflow" description="Selected work and case studies from Slinkflow." />
      <section className="relative overflow-hidden pt-40 pb-16 lg:pt-56 lg:pb-20">
        <div className="absolute -top-32 right-0 h-[520px] w-[520px] hero-gradient-orb" />
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <p className="text-xs uppercase tracking-[0.3em] text-cyan">Selected work</p>
          <h1 className="mt-6 max-w-5xl font-display text-[56px] font-light leading-[1.02] tracking-[-0.03em] md:text-[88px] lg:text-[112px]">
            <TextReveal text="Cases studied." />
            <br />
            <span className="gradient-text font-medium"><TextReveal text="Outcomes proven." delay={0.2} /></span>
          </h1>
          <FadeUp delay={0.4} className="mt-10 flex flex-wrap items-center gap-4">
            <div className="flex w-full max-w-md items-center gap-2 rounded-full border border-ink-100 bg-white px-5 py-3">
              <Search className="h-4 w-4 text-ink-400" />
              <input data-testid="portfolio-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search projects" className="flex-1 bg-transparent text-[15px] focus:outline-none" />
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map(c => (
                <button key={c} onClick={() => setCat(c)} data-testid={`portfolio-filter-${c.toLowerCase()}`} className={`rounded-full px-4 py-2 text-sm transition ${cat === c ? 'bg-ink-900 text-white' : 'border border-ink-100 bg-white text-ink-700 hover:border-cyan hover:text-cyan'}`}>{c}</button>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="pb-32 lg:pb-40">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="grid gap-8 md:grid-cols-2">
            {paged.map((p, i) => (
              <FadeUp key={p.id} delay={i * 0.05}>
                <Link to={`/portfolio/${p.slug}`} data-cursor data-cursor-label="View case" data-testid={`portfolio-card-${p.slug}`} className="group block">
                  <div className="relative overflow-hidden rounded-3xl bg-ink-50">
                    <div className="aspect-[4/3] overflow-hidden">
                      <img src={p.cover} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.4s] group-hover:scale-[1.06]" />
                    </div>
                    <span className="absolute left-4 top-4 rounded-full bg-white/85 backdrop-blur px-3 py-1.5 text-[11px] uppercase tracking-widest text-ink-700">{p.category}</span>
                  </div>
                  <div className="mt-6 flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-display text-[28px] text-ink-900">{p.title}</h3>
                      <p className="mt-1 text-[15px] text-ink-500">{p.tagline}</p>
                    </div>
                    <ArrowUpRight className="h-5 w-5 text-ink-400 transition-transform group-hover:rotate-45 group-hover:text-cyan" />
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>
          <Pagination page={page} total={filtered.length} perPage={PER_PAGE} onChange={setPage} />
        </div>
      </section>
    </>
  );
}
